import { useState } from "react";
import { Pressable, View, Text } from "react-native";
import { EMBER } from "../../onboarding/theme";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
export type FactSheetContextRow = {
  label: string;
  value: string;
};

function FactSheetContextInfoRow({ label, value }: FactSheetContextRow) {
  const isLongRow = label.length > 18 || value.length > 32;

  return (
    <View
      style={{
        flexDirection: isLongRow ? "column" : "row",
        alignItems: isLongRow ? "flex-start" : "center",
        justifyContent: "space-between",
        gap: isLongRow ? 3 : 14,
        paddingVertical: 9,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          fontWeight: "700",
          color: EMBER.colors.textLight,
          letterSpacing: 0,
          lineHeight: 16,
          width: isLongRow ? undefined : 110,
        }}
        numberOfLines={1}
      >
        {label}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "700",
          color: EMBER.colors.text,
          lineHeight: 18,
          flex: isLongRow ? undefined : 1,
          textAlign: isLongRow ? "left" : "right",
        }}
        numberOfLines={isLongRow ? 3 : 2}
      >
        {value}
      </Text>
    </View>
  );
}

export default function FactSheetContextCard({
  label,
  meta,
  rows,
  collapsedRowCount = 4,
  expandLabel,
  collapseLabel,
  actionLabel,
  onActionPress,
}: {
  label: string;
  meta?: string | null;
  rows: FactSheetContextRow[];
  collapsedRowCount?: number;
  expandLabel: string;
  collapseLabel: string;
  actionLabel?: string;
  onActionPress?: () => void;
}) {
  const capturePress = usePressCapture("FactSheetContextCard");
  const [expanded, setExpanded] = useState(false);
  const shouldCollapse = rows.length > collapsedRowCount;
  const visibleRows =
    shouldCollapse && !expanded ? rows.slice(0, collapsedRowCount) : rows;

  return (
    <View
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.34)",
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "rgba(44, 37, 33, 0.08)",
        paddingHorizontal: 14,
        paddingVertical: 12,
        marginBottom: 16,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "800",
            color: EMBER.colors.text,
            lineHeight: 18,
            flex: 1,
          }}
          numberOfLines={2}
        >
          {label}
        </Text>
        {meta ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              fontWeight: "700",
              color: EMBER.colors.textLight,
              lineHeight: 16,
              textAlign: "right",
              flexShrink: 0,
              maxWidth: "46%",
            }}
            numberOfLines={1}
          >
            {meta}
          </Text>
        ) : null}
      </View>

      {rows.length > 0 ? (
        <View
          style={{
            borderTopWidth: 1,
            borderTopColor: "rgba(44, 37, 33, 0.08)",
            marginTop: 10,
          }}
        >
          {visibleRows.map((row, index) => (
            <View
              key={`${row.label}-${index}`}
              style={{
                borderTopWidth: index === 0 ? 0 : 1,
                borderTopColor: "rgba(44, 37, 33, 0.06)",
              }}
            >
              <FactSheetContextInfoRow {...row} />
            </View>
          ))}
          {shouldCollapse ? (
            <Pressable
              accessibilityRole="button"
              onPress={() => {
                capturePress(
                  pressLabel(expanded ? collapseLabel : expandLabel),
                  expanded ? "collapse_context" : "expand_context",
                );
                setExpanded((current) => !current);
              }}
              style={({ pressed }) => ({
                borderTopWidth: 1,
                borderTopColor: "rgba(44, 37, 33, 0.06)",
                paddingTop: 10,
                opacity: pressed ? 0.65 : 1,
              })}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  fontWeight: "800",
                  color: EMBER.colors.textMid,
                  lineHeight: 16,
                }}
              >
                {expanded ? collapseLabel : expandLabel}
              </Text>
            </Pressable>
          ) : null}
        </View>
      ) : null}

      {actionLabel && onActionPress ? (
        <Pressable
          accessibilityRole="button"
          onPress={() => {
            capturePress(pressLabel(actionLabel), "fact_sheet_context_action");
            onActionPress();
          }}
          style={({ pressed }) => ({
            borderTopWidth: 1,
            borderTopColor: "rgba(44, 37, 33, 0.08)",
            paddingTop: 10,
            marginTop: rows.length > 0 ? 1 : 10,
            opacity: pressed ? 0.65 : 1,
          })}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              fontWeight: "800",
              color: EMBER.colors.textMid,
              lineHeight: 16,
            }}
          >
            {actionLabel}
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}
